/** Auth API bindings (M10). */
import { http } from "@/shared/api/http";

export interface User {
  id: string;
  email: string;
  name: string;
  /** Conversational identity — the name the Marketing Department uses
   *  to address the owner. NEVER used as a login credential. */
  preferred_name?: string;
  picture: string | null;
  email_verified: boolean;
  oauth_provider: "password" | "emergent_google" | null;
  /** Server-authoritative platform role; unrelated to workspace ownership. */
  is_dev_user?: boolean;
  /** Shared read-only showcase identity; never sufficient for customer writes. */
  is_public_demo_user?: boolean;
  /**
   * Optional workspace-level role hint surfaced in the Topbar. When the
   * backend does not project a role onto the current session, the UI
   * silently falls back to `"owner"`. This field is never a credential
   * and is not authoritative — memberships are the source of truth.
   */
  role_hint?: string;
}

export interface Membership {
  workspace_id: string;
  role: string;
  permissions: string[];
}

export interface AuthMe {
  user: User;
  memberships: Membership[];
}

export interface Tokens {
  access_token: string;
  refresh_token: string;
  token_type: string;
  user: User;
}

export const register = (
  body: { email: string; password: string; name: string },
): Promise<{ user: User; verify_token: string | null; delivery_available: boolean; message: string }> =>
  http("/auth/register", { method: "POST", body });

export const verifyEmail = (token: string): Promise<{ user: User }> =>
  http("/auth/email-verify/confirm", { method: "POST", body: { token } });

export const resendVerification = (email: string): Promise<{ ok: boolean; verify_token: string | null; delivery_available: boolean }> =>
  http("/auth/email-verify/request", { method: "POST", body: { email } });

export const login = (email: string, password: string): Promise<Tokens> =>
  http("/auth/login", { method: "POST", body: { email, password }, credentials: "include" });

export const refresh = (): Promise<Tokens> =>
  http("/auth/refresh", { method: "POST", credentials: "include" });

export const logout = (): Promise<{ ok: boolean }> =>
  http("/auth/logout", { method: "POST", credentials: "include" });

export const requestReset = (email: string): Promise<{ ok: boolean; reset_token: string | null; delivery_available: boolean }> =>
  http("/auth/password-reset/request", { method: "POST", body: { email } });

export const confirmReset = (token: string, new_password: string): Promise<{ user: User }> =>
  http("/auth/password-reset/confirm", { method: "POST", body: { token, new_password } });

export const exchangeEmergentSession = (session_id: string): Promise<Tokens> =>
  http("/auth/oauth/emergent/exchange", {
    method: "POST", body: { session_id }, credentials: "include",
  });

export const me = (): Promise<AuthMe> => http("/auth/me");

export const inviteMember = (
  ws: string, body: { email: string; role: string },
): Promise<{ invitation_token: string; email: string; role: string }> =>
  http(`/auth/workspaces/${ws}/invitations`, { method: "POST", body });

export const acceptInvitation = (token: string): Promise<{ workspace_id: string; role: string }> =>
  http("/auth/invitations/accept", { method: "POST", body: { token } });

/** Public-preview auto-login for the contest walkthrough. */
export interface DemoLoginResponse extends Tokens {
  workspace_id: string;
}
export const demoLogin = (): Promise<DemoLoginResponse> =>
  http("/demo/login", { method: "POST", credentials: "include" });
